import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import { Button, Collapse, Space } from 'antd';

import { defaultTheme } from '@/assets/themes';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { ICourseShelfModule, blockedStatus, enStatus } from '@/interfaces/course';
import { coursesApi } from '@/services/course';
import styled, { css, StyledProp } from '@/styled';

import ModulesFilters from './Filters';
import ModuleHeader from './Header';
import Lessons from './Lessons';

const Modules = (props: StyledProp) => {
  const { hash } = useParams<{ hash: string }>();
  const breakpoints = useBreakpoints();
  const [fetchModules, { data, isLoading, isFetching, isUninitialized }] =
    coursesApi.endpoints.getModules.useLazyQuery();
  const { state } = useLocation();

  const defaultModuleExpanded = state?.module as ICourseShelfModule;

  const fetchMore = () => {
    fetchModules({ courseHash: hash, params: { ...data?.page, page: data.page.page + 1 } });
  };

  useEffect(() => {
    if (isUninitialized) {
      fetchModules({ courseHash: hash });
    }
  }, [fetchModules, hash, isUninitialized]);

  useEffect(() => {
    if (!defaultModuleExpanded || isFetching) return;

    const moduleElement = document.getElementById(`${defaultModuleExpanded?.id}`);
    const headerHeight = defaultTheme.variables.headerHeight;
    const usableSpace = 100;
    const renderDelay = 50;

    if (moduleElement) {
      setTimeout(() => {
        window.scroll({
          top: window.pageYOffset + (moduleElement.getBoundingClientRect().top - headerHeight - usableSpace),
          behavior: 'smooth'
        });
      }, renderDelay);
    }
  }, [isFetching, defaultModuleExpanded]);

  if (isLoading || !Array.isArray(data?.data)) return null;

  return (
    <Space size={32} direction='vertical' className={props.className}>
      {!breakpoints.down('lg') && <ModulesFilters />}

      <Collapse expandIconPosition='end' bordered={false} defaultActiveKey={[defaultModuleExpanded?.id]} accordion>
        {data?.data?.map(module => {
          const isModuleBlocked = blockedStatus.includes(module?.status);
          const freeLessons = module?.lessons.some(l => l.status === enStatus.SOMETHING_FREE);
          const isDisabled = isModuleBlocked && !freeLessons ? 'disabled' : null;
          return (
            <Collapse.Panel key={module?.id} header={<ModuleHeader module={module} />} collapsible={isDisabled}>
              <Lessons module={module} />
            </Collapse.Panel>
          );
        })}
      </Collapse>

      {data.page.page < data.total_pages && data.total_rows > 1 && (
        <Button className='button' onClick={fetchMore} loading={isFetching}>
          Carregar Mais
        </Button>
      )}
    </Space>
  );
};

export default styled(Modules)(({ theme }) => {
  return css`
    width: 100%;
    .ant-space {
      width: 100%;
    }

    .ant-collapse {
      background-color: ${theme.variables.tabs.contentBackground};
      height: fit-content;
      min-width: 100%;
      overflow: hidden;
    }

    .button {
      margin-top: ${theme.space.pxToRem(16)};
      margin-left: calc(50% - 66px);
      &[disabled] {
        display: none;
      }
    }

    .ant-typography {
      margin: 0;
      line-height: 100%;
    }

    .ant-progress,
    .ant-progress-inner {
      width: 40px !important;
      height: 40px !important;
    }

    .ant-collapse-header {
      padding: ${theme.space.pxToRem(24)} !important;
      height: 93px;
      align-items: center !important;
    }

    .ant-collapse-header[aria-expanded='false'] {
      .ant-collapse-arrow svg {
        transform: rotate(90deg);
      }
    }

    .ant-collapse-header-text {
      display: flex;
      align-items: center;
      column-gap: ${theme.space.pxToRem(24)};
      max-width: calc(100% - 28px);
      overflow: hidden;
    }

    .ant-collapse-header[aria-expanded='true'] {
      .ant-collapse-arrow svg {
        transform: rotate(-90deg) !important;
      }
    }

    .ant-collapse-content-box {
      padding: ${theme.space.spacing(0, 6, 6, 10)} !important;
      border-bottom: 1px solid ${theme.variables.borderColor};

      ${theme.breakpoints.down('md')} {
        padding: ${theme.space.spacing(0, 6, 6, 6)} !important;
        gap: ${theme.space.pxToRem(24)};
        display: flex;
        flex-direction: column;
      }
    }

    .ant-collapse-item {
      border-bottom: 1px solid ${theme.variables.borderColor};
    }
  `;
});
